import { useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAgentFilters } from '../../contexts/AgentFilterContext';
import { clearCopilotScope, setCopilotScope } from '../../services/copilot/copilotScopeStore';

/**
 * Pages of each Copilot layout whose data is narrowed by the scope: every one
 * draws the agent ("App" for M365) and user pickers, so nothing is narrowed out
 * of sight. Any other route — and any route added later — sends no scope:
 *
 *   posture         Access Management: findings carry no app or user (date only)
 *   recommendations Atlas inbox, no pickers; its evidence drawer reads the
 *                   module service and must not be narrowed by a hidden choice
 *   agent-network   M365: the Atlas graph page with its own range, no pickers
 *                   (Studio draws the pickers there, so it is scoped)
 *   usage-roi       FinOps service, not this module's
 *   alerts          alert service
 *   settings        config only
 *   audit-report    its own report filters against the compliance service
 *   security/*      security service, scoped by surface
 *
 * Compliance is listed: it shows the pickers and passes the same selection to
 * the compliance service as agent_id / user_email.
 */
const SHARED_SCOPED = [
  'dashboard',
  'users',
  'knowledge-gaps',
  'intent-analysis',
  'conversations',
  'threats',
  'sources',
  'registry',
  'log-explorer',
  'compliance',
];

export const COPILOT_SCOPED_ROUTES = Object.freeze({
  copilot: Object.freeze([...SHARED_SCOPED]),
  copilot_studio: Object.freeze([...SHARED_SCOPED, 'agent-network']),
});

/** True when `pathname` is one of `routes` directly under `routeBase`. */
export const isScopedPath = (pathname = '', routeBase = '', routes = []) => {
  if (!pathname.startsWith(`${routeBase}/`)) return false;
  const first = pathname.slice(routeBase.length + 1).split('/')[0];
  return routes.includes(first);
};

/**
 * Copies the layout's agent / user selection into copilotScopeStore — on the
 * scoped routes only — so the module's HTTP client narrows every request, and
 * clears it on any other route and on unmount. A layout effect, so the store is
 * current before any page's data effect for the same commit runs.
 */
const CopilotScopeSync = ({ module, routeBase }) => {
  const { filters } = useAgentFilters();
  const { pathname } = useLocation();
  const active = isScopedPath(pathname, routeBase, COPILOT_SCOPED_ROUTES[module] || []);

  useLayoutEffect(() => {
    if (active) setCopilotScope(module, filters);
    else clearCopilotScope(module);
  }, [module, active, filters]);

  useLayoutEffect(() => () => clearCopilotScope(module), [module]);

  return null;
};

export default CopilotScopeSync;
